/**
 * Edpies Focus Mode — micro-celebration helper.
 *
 * Watches a `correctCount` (running tally of correct answers in the current
 * Focus Mode session) and triggers a confetti burst + congrats banner the
 * first time it crosses each MILESTONE.
 *
 * Usage:
 *   <FocusCelebration correctCount={stats.correctAnswers} />
 *
 * Drop-in safe — renders nothing until a milestone hits, then mounts an
 * absolutely-positioned banner that auto-dismisses after 2.6s. Confetti
 * fires from the top centre using `canvas-confetti`. Each milestone fires
 * exactly once per component lifetime (which resets when the parent
 * unmounts — e.g., starting a new session).
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Sparkles, Trophy, Crown } from 'lucide-react';

const MILESTONES = [
  { count: 5, title: 'Nice start!', message: '5 correct in a row — keep that momentum!', Icon: Sparkles, accent: 'from-emerald-400 to-teal-500' },
  { count: 10, title: 'On fire!', message: '10 correct — you are crushing this session.', Icon: Trophy, accent: 'from-orange-400 to-rose-500' },
  { count: 25, title: 'Legendary!', message: '25 correct in a single session. Take a bow.', Icon: Crown, accent: 'from-amber-300 to-fuchsia-500' },
];

/** Fire a celebratory burst originating from the top-centre of the viewport. */
const fireConfetti = () => {
  const defaults = {
    spread: 75,
    ticks: 80,
    gravity: 0.9,
    decay: 0.94,
    startVelocity: 32,
    origin: { x: 0.5, y: 0.15 },
    colors: ['#a855f7', '#22d3ee', '#f59e0b', '#10b981', '#f43f5e', '#3b82f6'],
  };
  confetti({ ...defaults, particleCount: 70, scalar: 1.1 });
  setTimeout(() => confetti({ ...defaults, particleCount: 50, scalar: 0.9, origin: { x: 0.25, y: 0.2 } }), 180);
  setTimeout(() => confetti({ ...defaults, particleCount: 50, scalar: 0.9, origin: { x: 0.75, y: 0.2 } }), 320);
};

const FocusCelebration = ({ correctCount = 0 }) => {
  const firedRef = useRef(new Set());
  const [active, setActive] = useState(null); // current MILESTONE being shown

  useEffect(() => {
    const hit = MILESTONES.find((m) => correctCount >= m.count && !firedRef.current.has(m.count));
    if (!hit) return;
    firedRef.current.add(hit.count);
    setActive(hit);
    // safely no-op on SSR / environments without canvas
    try { fireConfetti(); } catch { /* ignore */ }
  }, [correctCount]);

  // Auto-dismiss the banner 2.6s after a milestone fires. Kept in a separate
  // effect so that subsequent correctCount changes don't reset / cancel the
  // dismiss timer (which previously caused the banner to linger indefinitely).
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setActive(null), 2600);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0, y: -24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 320, damping: 24 }}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          data-testid="focus-celebration-banner"
          role="status"
          aria-live="polite"
        >
          <div className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-2xl bg-gradient-to-r ${active.accent} text-white font-semibold`}>
            <active.Icon className="h-5 w-5" />
            <div className="leading-tight">
              <p className="text-sm font-bold">{active.title}</p>
              <p className="text-xs opacity-90">{active.message}</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FocusCelebration;
